/**
 * useCitationLink.jsx — React hook version of resolve.js's renderCitationLink.
 *
 * Same resolution logic (folder map, ticker exceptions, page padding), but:
 *   - no `fs` / CLI — this runs in the browser
 *   - the padding lookup table is bundled locally (static import) instead of read via `fs`
 *     at import time or fetched at runtime — so resolution is fully synchronous, no
 *     loading state needed
 *   - the public API is a hook: useCitationLink(rawCitation, ticker) -> ReactNode[]
 *
 * Unlike the earlier version, this hook does the rendering itself: instead of handing
 * back a plain { linkable, url, scope, ... } object per document, it returns an array
 * of ready-to-drop-in JSX elements — one <a> per linkable document reference.
 *
 * IMPORTANT: these are plain <a> tags, not react-router's <Link>. Every url this
 * resolves to is an absolute external link (https://storage.googleapis.com/...).
 * <Link to={externalUrl}> will NOT navigate to it — react-router intercepts the click
 * and pushes it as an internal route instead, breaking the link silently. Do not swap
 * these <a> tags for <Link> even if the rest of the app uses react-router elsewhere.
 *
 * Non-linkable references (missing docType/year/ticker) are skipped from the returned
 * array — filter them out upstream if you need to show a fallback for those instead.
 *
 * ---- Usage in a component ----
 *
 *   function CitationCell({ citation, ticker }) {
 *     const links = useCitationLink(citation, ticker);
 *     return links.length ? links : <span>{citation}</span>;
 *   }
 */
import { useMemo } from "react";
import { parseCitation } from "./helperFunctions/parseCitation.js"; // same parser resolve.js used — must be browser-safe
// Bundled at build time. Adjust the path to wherever the config actually lives.
// If the file may not exist for older data, keep this import behind a try/catch at the
// bundler level, or ship an empty {} placeholder file — either way PADDING_TABLE must
// resolve to an object (missing entries just fall back to DEFAULT_PADDING, same as before).
import PADDING_TABLE from "../assets/citationPadding.json";
import { Tooltip } from "antd";

const BUCKET_BASE = "https://storage.googleapis.com/source-filings";
const DEFAULT_PADDING = 8; //Previously 8, average padding number, possibly 1?

// DECISION 1 (unchanged) — docType -> storage folder, one place only.
const FOLDER = {
  proxy: "ProxyStatements",
  annual: "AnnualReports",
  initial: "InitialReports",
};

// DECISION 3 (unchanged) — share-class ticker exceptions are a lookup table, not logic.
const TICKER_EXCEPTIONS = {
  GOOGL: "GOOG", // Alphabet
  "BRK.B": "BRK.A", // Berkshire Hathaway
  UA: "UAA", // Under Armour
};
function tickerToFilename(ticker) {
  return TICKER_EXCEPTIONS[ticker] || ticker;
}

function leadingTicker(citation) {
  const m = citation.match(/^([A-Z][A-Z.]{0,5})\s+(?:19|20)\d{2}/);
  return m ? m[1] : null;
}

/** Label shown for a resolved link: "p. 63" for a page-level link, "full report" otherwise. */
function labelFor(scope, originalCitation, pageNumber, isFirstCitation) {
  return scope === "page"
    ? isFirstCitation
      ? `${originalCitation} p.${pageNumber}`
      : `, p. ${pageNumber}`
    : `${originalCitation}, Full Report`;
}

/**
 * Pure resolver — identical to resolve.js's resolve(), but takes the padding table as an
 * argument instead of reading a module-level variable populated by fs.
 */
function resolveRef(ref, paddingTable, originalCitation) {
  const folder = FOLDER[ref.docType];
  if (!folder || !ref.year || !ref.ticker) {
    return {
      linkable: false,
      url: null,
      reason: ref.reason || "missing docType, year, or ticker",
    };
  }
  const file = `${encodeURIComponent(tickerToFilename(ref.ticker))}.pdf`;
  const base = `${BUCKET_BASE}/${ref.year}/${folder}/${file}`;

  // console.log(ref);
  if (ref.pages?.length) {
    const allCitations = [];
    let isFirstCitation = true;
    for (const page of ref.pages) {
      const cited = page;
      const key = base.replace(`${BUCKET_BASE}/`, "").replace(/\.pdf$/, "");
      const measured = paddingTable[key];
      const padding = measured ?? DEFAULT_PADDING;
      const physical = cited + padding;
      const originalCitationWithoutPageNumber = ref?.splitCitation;
      allCitations.push({
        linkable: true,
        pageResolved: true,
        scope: "page",
        page: cited,
        physicalPage: physical,
        paddingSource: measured !== undefined ? "measured" : "default",
        url: `${base}#page=${physical}#citationPage=${cited}`,
        label: labelFor(
          "page",
          originalCitationWithoutPageNumber,
          cited,
          isFirstCitation,
        ),
        urlLabel: labelFor(
          "page",
          originalCitationWithoutPageNumber,
          cited,
          true,
        ),
      });
      isFirstCitation = false;
    }
    return allCitations;
  }
  return [
    {
      linkable: true,
      pageResolved: false,
      scope: "document",
      url: base,
      label: labelFor("document", originalCitation),
      hoverTextDetails:
        "Opens the full report — the citation didn't specify a page.",
    },
  ];
}

/** Same multi-document ";"-splitting behavior as resolveCitation() in resolve.js. */
function resolveCitationRefs(rawCitation, ticker, paddingTable) {
  const segs = rawCitation
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean)
    .map(parseCitation);
  const docs = segs.filter((p) => p.docType);
  if (docs.length === 0) {
    return [
      resolveRef({ ...(segs[0] || {}), ticker }, paddingTable, rawCitation),
    ];
  }
  return docs.map((p) =>
    resolveRef(
      { ...p, ticker },
      paddingTable,
      segs?.splitCitation || rawCitation,
    ),
  );
}

/**
 * useCitationLink(citation, ticker) -> ReactNode[]
 *
 * Prefer passing the authoritative ticker from page/company context; falls back to the
 * leading token in the citation text (same rule as before — needed for the ~20 name-led
 * citations).
 *
 * Fully synchronous — PADDING_TABLE is bundled at build time, so there's no loading/error
 * state to thread through consumers.
 *
 * Returns one <a> element per linkable document referenced in the citation (usually
 * length 1, but a citation can reference multiple documents split on ";"). Non-linkable
 * references are omitted — if you need to surface those, resolve manually with the same
 * building blocks or ask for a version that returns both.
 */
export function useCitationLink(citation, ticker, fiscalYear = "") {
  return useMemo(() => {
    // console.log(fiscalYear);
    if (!citation) return [];
    let revisedCitation = citation;
    if (fiscalYear === "FY 2024") {
      revisedCitation = revisedCitation.replace("2024", "2025");
    }
    if (fiscalYear === "FY 2025") {
      revisedCitation = revisedCitation.replace("2025", "2026");
    }
    const tk = ticker || leadingTicker(revisedCitation);
    const refs = resolveCitationRefs(revisedCitation, tk, PADDING_TABLE);
    // console.log(refs.flat());
    return refs
      .flat()
      .filter((link) => link.linkable)
      .map((link, i) => (
        <Tooltip
          title={`Citation links are in development. Links to exact pages will improve over time. ${link?.hoverTextDetails || ""}`}
        >
          <span>
            <a
              key={link.url ?? i}
              href={link.url}
              target="_blank"
              rel="noreferrer"
              title={link.urlLabel}
            >
              {link.label}
            </a>
          </span>
        </Tooltip>
      ));
  }, [citation, ticker, fiscalYear]);
}

// Backward-compatible alias, in case other files already import the old name.
export const useRenderCitationLink = useCitationLink;
